/**
 * useNotificationSubscription — WS subscribe `/ws/notifications` (P3-S3 Doc 44).
 *
 * Mount 1 lần ở AppShell khi authenticated. WS handshake auth bằng JWT,
 * receive notification events từ Redis pubsub forward → bump store.
 *
 * Reconnect tự động qua reconnecting-websocket (giống chat WS pattern).
 * Polling fallback (30s trong NotificationBell) vẫn hoạt động ngay cả
 * khi WS rớt → UX không break.
 */
import * as React from "react";
import ReconnectingWebSocket from "reconnecting-websocket";

import { useAuthStore } from "@/stores/auth";
import { useNotificationRealtimeStore } from "@/stores/notificationRealtime";

const WS_BASE = import.meta.env.VITE_WS_BASE_URL ?? "ws://localhost:8002";

interface NotificationWsEvent {
  type?: string;
  id?: string;
  title?: string;
  body?: string;
  payload?: Record<string, unknown>;
}

export function useNotificationSubscription(): void {
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const bump = useNotificationRealtimeStore((s) => s.bump);
  const setWsStatus = useNotificationRealtimeStore((s) => s.setWsStatus);

  React.useEffect(() => {
    if (!isAuth || !accessToken) {
      setWsStatus("idle");
      return;
    }

    const url = `${WS_BASE}/ws/notifications`;
    setWsStatus("connecting");

    const ws = new ReconnectingWebSocket(url, [], {
      maxRetries: 20,
      connectionTimeout: 4000,
      maxReconnectionDelay: 30000,
      minReconnectionDelay: 1000,
      reconnectionDelayGrowFactor: 1.5,
    });

    let pingTimer: number | null = null;

    ws.addEventListener("open", () => {
      try {
        ws.send(JSON.stringify({ type: "auth", token: accessToken }));
      } catch {
        // ignore
      }
    });

    ws.addEventListener("message", (ev: MessageEvent) => {
      try {
        const data = JSON.parse(String(ev.data)) as NotificationWsEvent;
        if (data.type === "connected") {
          setWsStatus("connected");
          // Setup keepalive ping
          if (pingTimer !== null) window.clearInterval(pingTimer);
          pingTimer = window.setInterval(() => {
            try {
              ws.send(JSON.stringify({ type: "ping" }));
            } catch {
              // ignore
            }
          }, 30_000);
          return;
        }
        if (data.type === "notification") {
          bump({ id: data.id, title: data.title, body: data.body });
          return;
        }
        if (data.type === "error") {
          // Auth fail → close + don't auto-reconnect (token sẽ refresh sau)
          setWsStatus("disconnected");
          return;
        }
      } catch {
        // ignore parse errors
      }
    });

    ws.addEventListener("close", () => {
      setWsStatus("disconnected");
      if (pingTimer !== null) {
        window.clearInterval(pingTimer);
        pingTimer = null;
      }
    });

    ws.addEventListener("error", () => {
      setWsStatus("disconnected");
    });

    return () => {
      if (pingTimer !== null) {
        window.clearInterval(pingTimer);
      }
      try {
        ws.close();
      } catch {
        // ignore
      }
      setWsStatus("idle");
    };
  }, [accessToken, isAuth, bump, setWsStatus]);
}
